import { useState, useEffect } from 'react'
import _ from 'lodash'
import { Input, Grid, Tooltip, Separator } from '@design-systems/canvas-ui'
import { AutocompleteInput } from '../../common/AutocompleteInput'
import {
  buildDropdownOptions,
  getReactionGridDataField,
  fetchGroupName,
  getDropdownOptions,
} from '../../common/helper'
import { renderRFI3QuestionWindow } from '../../common/rfiHelper'
import ReactionGrid from '../../common/ReactionGrid'
import { SectionWrapper, renderControl } from '../Builders'
import FactoryInput from './FactoryInputSelector'
import { RFI_TYPE_SUPPLIER } from '../../common/commonConstants'
import { getRfiTagValues } from '../../common/common'
import Icon, { InfoIcon } from '@design-systems/react-icons'

const BPC_SECTION_NAME = 'beauty_packaging_capabilities'
const controlGroups = [
  {
    uid: 'conduct_ideation',
    group:
      'How do you conduct Ideation for Brand , Product concept & New Innovation ?',
    controls: [
      {
        uid: 'in_house',
        type: 'radio_button_group',
        options: 'common.ideations',
        label_name: 'In-house',
        row: 1,
        column: 1,
        width: 3,
        width_percentage: 100,
      },
      {
        uid: 'client_resource',
        type: 'radio_button_group',
        options: 'common.ideations',
        label_name: 'Client Resources',
        row: 1,
        column: 2,
        width: 3,
        width_percentage: 100,
      },
      {
        uid: 'outsourced',
        type: 'radio_button_group',
        options: 'common.ideations',
        label_name: 'Outsourced',
        row: 1,
        column: 3,
        width: 3,
        width_percentage: 100,
      },
    ],
  },
  {
    uid: 'product_claims_packaging',
    controls: [
      {
        uid: 'product_claims_packaging',
        type: 'reaction_grid',
        options: 'beauty_packaging_capabilities',
        label_name: 'Products Claims Packaging 2',
        info: getRfiTagValues('rfi1InfoIconMessages')[BPC_SECTION_NAME][
          'product_claims_packaging'
        ],
        row: 1,
        column: 1,
        width: 8,
        width_percentage: 100,
      },
    ],
  },
  {
    uid: 'customization',
    group: 'Customisation Tools',
    controls: [
      {
        uid: 'customization',
        type: 'radio_button_group',
        options: 'common.yes_no',
        label_name: 'Can you customize packaging colors?',
        row: 1,
        column: 1,
        width: 3,
        width_percentage: 100,
      },
    ],
  },
]

const controlGroupsForReadOnly = [
  {
    uid: 'conduct_ideation',
    group:
      'How do you conduct Ideation for Brand , Product concept & New Innovation ?',
    controls: [
      {
        uid: 'in_house',
        type: 'label_readonly_header',
        label_name: 'In-house',
        row: 1,
        column: 1,
        width: 3,
        width_percentage: 100,
      },
      {
        uid: 'client_resource',
        type: 'label_readonly_header',
        label_name: 'Client Resources',
        row: 1,
        column: 2,
        width: 3,
        width_percentage: 100,
      },
      {
        uid: 'outsourced',
        type: 'label_readonly_header',
        label_name: 'Outsourced',
        row: 1,
        column: 3,
        width: 3,
        width_percentage: 100,
      },
    ],
  },
  {
    uid: 'product_claims_packaging',
    controls: [
      {
        uid: 'product_claims_packaging',
        type: 'reaction_grid',
        options: 'beauty_packaging_capabilities',
        label_name: 'Products Claims Packaging 2',
        row: 1,
        column: 1,
        width: 8,
        width_percentage: 100,
      },
    ],
  },
  {
    uid: 'customization',
    group: 'Customisation Tools',
    controls: [
      {
        uid: 'customization',
        type: 'label_readonly_header',
        label_name: 'Can you customize packaging colors?',
        row: 1,
        column: 1,
        width: 3,
        width_percentage: 100,
      },
    ],
  },
]

const BeautyPackagingCapabilities = (props) => {
  const SECTION_INHOUSE_CAP_IND_DESIGN = 'in_house_capabilities'
  const SECTION_PRODUCTION = 'production'
  const IDEATION_PROCESS = 'conduct_ideation'
  const SECTION_PROD_CLAIMS = 'product_claims_packaging'
  const FIXED_REACTION_GRIDS = [SECTION_PROD_CLAIMS]
  const OWNED_PACKAGING_FACTORIES = 'owned_packaging_factories'
  const INDUSTRIAL_DESIGN_CAPABILITIES = 'industrial_design_capabilities'
  const CUSTOMIZATION = 'customization'

  const [beautyPkgCapabilities, setBeautyPkgCapabilities] = useState(
    props.sectionData,
  )

  //Trigger save
  const [isTriggerSave, setIsTriggerSave] = useState(false)

  useEffect(() => {
    if (isTriggerSave && !_.isEqual(props.sectionData, beautyPkgCapabilities)) {
      props.saveRfiPayload(beautyPkgCapabilities, props.sectionTemplate.uid)
    }
  }, [isTriggerSave]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateSelectedBeautyPackagingData = (propertyId, selectedRGs) => {
    let groupRDList = []
    // eslint-disable-next-line array-callback-return
    selectedRGs.map((eachSelectedRG) => {
      let foundRGDetails = beautyPkgCapabilities[propertyId].filter(
        (eachExistingRG) =>
          eachExistingRG.display_name === eachSelectedRG.value,
      )
      if (foundRGDetails.length) {
        groupRDList.push(foundRGDetails[0])
      } else {
        let masterGroupId =
          props.rfiMasterData?.common.beauty_packaging_capabilities[
            propertyId
          ].filter(
            (eachMasterRG) =>
              eachMasterRG.display_name === eachSelectedRG.value,
          )[0]?.group_name
        groupRDList.push({
          group_name: masterGroupId,
          display_name: eachSelectedRG.value,
          capabilities: [],
        })
      }
    })

    setBeautyPkgCapabilities({
      ...beautyPkgCapabilities,
      [propertyId]: groupRDList,
    })
  }

  /**
   * Dynamically generate entire section reaction grids
   * @param {*} section
   * @returns
   */
  const createSectionReactionGrids = (section) => {
    if (beautyPkgCapabilities[section]) {
      return beautyPkgCapabilities[section]
        .map((e) => e.display_name)
        .map((item, index) => (
          <Grid.Item
            size={{ xs: 8 }}
            id={`grid-rg-${section}-${item}`}
            key={`grid-rg-${section}-${item}`}
            className="hc8-pt-2x"
          >
            <ReactionGrid
              id={`rg-${section}-${index}-${item}`}
              heading={getReactionGridDataField(
                props.rfiMasterData?.common.beauty_packaging_capabilities,
                beautyPkgCapabilities,
                section,
                fetchGroupName(
                  props.rfiMasterData?.common,
                  BPC_SECTION_NAME,
                  section,
                  item,
                ),
                'display_name',
                props.isReadonly,
              )}
              fields={getReactionGridDataField(
                props.rfiMasterData?.common.beauty_packaging_capabilities,
                beautyPkgCapabilities,
                section,
                fetchGroupName(
                  props.rfiMasterData?.common,
                  BPC_SECTION_NAME,
                  section,
                  item,
                ),
                'rows',
                props.isReadonly,
              )}
              options={getReactionGridDataField(
                props.rfiMasterData?.common.beauty_packaging_capabilities,
                beautyPkgCapabilities,
                section,
                fetchGroupName(
                  props.rfiMasterData?.common,
                  BPC_SECTION_NAME,
                  section,
                  item,
                ),
                'title',
                props.isReadonly,
              )}
              value={
                beautyPkgCapabilities[section].filter(
                  (groups) => groups.display_name === item,
                )[0]?.capabilities
              }
              section={section}
              isreadOnly={props.isReadonly}
              groupName={fetchGroupName(
                props.rfiMasterData?.common,
                BPC_SECTION_NAME,
                section,
                item,
              )}
              updateGridValues={props.isReadonly ? () => {} : updateGridValues}
              removeGrid={props.isReadonly ? () => {} : removeGrid}
              allowRemove={!props.isReadonly}
            ></ReactionGrid>
            <Separator className="hc8-pt-6x" />
          </Grid.Item>
        ))
    }
  }

  const removeGrid = (heading, groupUID, sectionUID) => {
    let newRGSet = beautyPkgCapabilities[sectionUID]
      .filter((e) => e.group_name !== groupUID)
      .map((e) => e)

    setBeautyPkgCapabilities({
      ...beautyPkgCapabilities,
      [sectionUID]: newRGSet,
    })
  }

  const updateGridValues = (groupName, displayName, section, valueMatrix) => {
    const newData = beautyPkgCapabilities[section].map(
      (eachGrid, gridIndex) => {
        return eachGrid.group_name !== groupName
          ? eachGrid
          : { ...eachGrid, capabilities: valueMatrix }
      },
    )
    setBeautyPkgCapabilities({
      ...beautyPkgCapabilities,
      [section]: newData,
    })
  }

  const onInputChangeHandler = (value, metadata) => {
    let groupUID = metadata?.inputMetadata?.groupUID
    switch (groupUID) {
      case OWNED_PACKAGING_FACTORIES:
        setBeautyPkgCapabilities({
          ...beautyPkgCapabilities,
          [OWNED_PACKAGING_FACTORIES]: value,
        })
        break
      case SECTION_PROD_CLAIMS: {
        let newData = [
          {
            group_name: metadata.groupName,
            display_name: metadata.displayName,
            capabilities: value,
          },
        ]

        setBeautyPkgCapabilities({
          ...beautyPkgCapabilities,
          [SECTION_PROD_CLAIMS]: newData,
        })
        break
      }
      case CUSTOMIZATION:
        setBeautyPkgCapabilities({
          ...beautyPkgCapabilities,
          customization: value,
        })
        break
      default:
        setBeautyPkgCapabilities({
          ...beautyPkgCapabilities,
          [groupUID]: {
            ...beautyPkgCapabilities[groupUID],
            [metadata.uid]: value,
          },
        })
    }
  }

  const renderBPCTemplateControls = (groupUID) => {
    const controlGroupsForComputation = props.isReadonly
      ? controlGroupsForReadOnly
      : controlGroups
    let controlGroup = controlGroupsForComputation.filter(
      (eachGroup) => eachGroup.uid === groupUID,
    )[0]

    if (controlGroup) {
      let groupControls = controlGroup.controls.map((eachControl) => {
        let mutatedRowControl = Object.assign({}, eachControl)
        let controlMetadata = {}
        controlMetadata.groupUID = groupUID
        controlMetadata.isreadOnly = props.isReadonly
        mutatedRowControl.metadata = controlMetadata

        let masterData = FIXED_REACTION_GRIDS.includes(groupUID)
          ? props.rfiMasterData.common
          : props.rfiMasterData

        let controlData = FIXED_REACTION_GRIDS.includes(groupUID)
          ? beautyPkgCapabilities[groupUID]?.[0]
          : groupUID === CUSTOMIZATION
            ? beautyPkgCapabilities
            : beautyPkgCapabilities[groupUID]

        return (
          <Grid.Item size={{ xs: eachControl.width }}>
            {renderControl(
              mutatedRowControl,
              controlData,
              masterData,
              props.isReadonly ? () => {} : onInputChangeHandler,
            )}
          </Grid.Item>
        )
      })

      return (
        <div>
          {controlGroup.group && (
            <Input.Label className="hc8-fs-150 hc8-mt-none font-weight-500">
              {controlGroup.group}
            </Input.Label>
          )}
          <Grid.Container className="hc8-mt-1x hc8-mb-1x">
            {groupControls}
          </Grid.Container>
        </div>
      )
    }
  }

  const renderBeautyPackagingBody = () => {
    let sectionContent = (
      <Grid.Container>
        {/* 1. Ideation */}
        {props.rfiType === RFI_TYPE_SUPPLIER && (
          <Grid.Item
            size={{ xs: 12 }}
            className={props.isReadonly ? 'hc8-pb-none' : ''}
          >
            {renderBPCTemplateControls(IDEATION_PROCESS)}
          </Grid.Item>
        )}

        {props.rfiType === RFI_TYPE_SUPPLIER ? (
          <Grid.Item
            size={{ xs: 12 }}
            className={props.isReadonly ? 'hc8-pv-none' : ''}
          >
            {/* 2. In-House Capabilities */}
            <div className="align-centre-left hc8-mt-none">
              <Input.Label className="hc8-fs-150 hc8-mt-2x font-weight-500">
                In House Capabilities
              </Input.Label>
              <Tooltip
                className="hc8-ml-2x"
                content={
                  getRfiTagValues('rfi1InfoIconMessages')[BPC_SECTION_NAME][
                    'inhouseOutsourceCapabilities'
                  ]
                }
                location="right"
              >
                <Icon className="row-centre" svg={InfoIcon} />
              </Tooltip>
            </div>
            <Input.Label className="hc8-mt-4x hc8-fs-100 hc8-mb-2x">
              Select All Formulation Category
            </Input.Label>
            <Grid.Item size={{ xs: 4 }}>
              <AutocompleteInput
                value={buildDropdownOptions(
                  beautyPkgCapabilities[SECTION_INHOUSE_CAP_IND_DESIGN]?.map(
                    (e) => e.display_name,
                  ),
                  null,
                  false,
                  false,
                )}
                readOnly={props.isReadonly}
                multiselect={true}
                class="hc8-fs-150"
                options={getDropdownOptions(
                  SECTION_INHOUSE_CAP_IND_DESIGN,
                  props.rfiMasterData.common,
                  BPC_SECTION_NAME,
                )}
                onUpdate={(value) => {
                  updateSelectedBeautyPackagingData(
                    SECTION_INHOUSE_CAP_IND_DESIGN,
                    value,
                  )
                }}
              />
            </Grid.Item>
            <Grid.Item
              className={props.isReadonly ? 'hc8-pt-2x' : 'hc8-mt-none'}
            >
              {createSectionReactionGrids(SECTION_INHOUSE_CAP_IND_DESIGN)}
            </Grid.Item>
          </Grid.Item>
        ) : (
          <Grid.Item size={{ xs: 12 }}>
            {/* 2. Industrial Design */}
            <div className="align-centre-left hc8-mt-4x">
              <Input.Label className="hc8-fs-150 hc8-mt-2x font-weight-500">
                Industrial Design
              </Input.Label>
              <Tooltip
                className="hc8-ml-2x"
                content={
                  getRfiTagValues('rfi1InfoIconMessages')[BPC_SECTION_NAME][
                    'industrial_design'
                  ]
                }
                location="right"
              >
                <Icon className="row-centre" svg={InfoIcon} />
              </Tooltip>
            </div>
            <Input.Label className="hc8-mt-4x hc8-fs-100 hc8-mb-2x">
              Select All Industrial Design Capabilities
            </Input.Label>
            <Grid.Item size={{ xs: 4 }}>
              <AutocompleteInput
                value={buildDropdownOptions(
                  beautyPkgCapabilities[INDUSTRIAL_DESIGN_CAPABILITIES]?.map(
                    (e) => e.display_name,
                  ),
                  null,
                  false,
                  false,
                )}
                readOnly={props.isReadonly}
                multiselect={true}
                class="hc8-fs-150"
                options={getDropdownOptions(
                  INDUSTRIAL_DESIGN_CAPABILITIES,
                  props.rfiMasterData.common,
                  BPC_SECTION_NAME,
                )}
                onUpdate={(value) => {
                  updateSelectedBeautyPackagingData(
                    INDUSTRIAL_DESIGN_CAPABILITIES,
                    value,
                  )
                }}
              />
            </Grid.Item>
            <Grid.Item className="hc8-mt-none">
              {createSectionReactionGrids(INDUSTRIAL_DESIGN_CAPABILITIES)}
            </Grid.Item>
          </Grid.Item>
        )}

        {/* 3. Production */}
        <Grid.Item
          className={props.isReadonly ? 'hc8-pv-none' : 'hc8-mt-6x'}
          size={{ xs: 12 }}
        >
          <div className="align-centre-left hc8-mt-none">
            <Input.Label className="hc8-fs-150 hc8-mt-2x font-weight-500">
              Production
            </Input.Label>
            <Tooltip
              className="hc8-ml-2x"
              content={
                getRfiTagValues('rfi1InfoIconMessages')[BPC_SECTION_NAME][
                  'packaging'
                ]
              }
              location="right"
            >
              <Icon className="row-centre" svg={InfoIcon} />
            </Tooltip>
          </div>
          <Input.Label className="hc8-mt-4x hc8-fs-100 hc8-mb-2x">
            Select All Product Claims
          </Input.Label>
          <Grid.Item size={{ xs: 4 }}>
            <AutocompleteInput
              value={buildDropdownOptions(
                beautyPkgCapabilities[SECTION_PRODUCTION]?.map(
                  (e) => e.display_name,
                ),
                null,
                false,
                false,
              )}
              readOnly={props.isReadonly}
              multiselect={true}
              class="hc8-fs-150"
              options={getDropdownOptions(
                SECTION_PRODUCTION,
                props.rfiMasterData.common,
                BPC_SECTION_NAME,
              )}
              onUpdate={(value) => {
                updateSelectedBeautyPackagingData(SECTION_PRODUCTION, value)
              }}
            />
          </Grid.Item>
          <Grid.Item className={props.isReadonly ? 'hc8-pt-2x' : 'hc8-mt-none'}>
            {createSectionReactionGrids(SECTION_PRODUCTION)}
          </Grid.Item>
        </Grid.Item>

        {/* 4. Product claim packaging */}
        <Grid.Item
          size={{ xs: 12 }}
          className={props.isReadonly ? 'hc8-pv-none' : ''}
        >
          {renderBPCTemplateControls(SECTION_PROD_CLAIMS)}
        </Grid.Item>

        {/* 5. Customization Tools */}
        <Grid.Item
          size={{ xs: 12 }}
          className={props.isReadonly ? 'hc8-pt-2x' : ''}
        >
          {renderBPCTemplateControls(CUSTOMIZATION)}
        </Grid.Item>

        {/* 6. Owned Packaging Factory */}
        {props.rfiType === RFI_TYPE_SUPPLIER && (
          <Grid.Item size={{ xs: 12 }} className="hc8-pv-none">
            <FactoryInput
              subSectionName={'Owned Packaging Factories'}
              subSectionLabel={
                'Please provide the names of any owned packaging factories and the packaging types they produce'
              }
              factoryData={beautyPkgCapabilities[OWNED_PACKAGING_FACTORIES]}
              groupUID={OWNED_PACKAGING_FACTORIES}
              rfiMasterData={props.rfiMasterData}
              updateFactoryData={onInputChangeHandler}
              isReadonly={props.isReadonly}
              errorCache={props.errorCache}
            />
          </Grid.Item>
        )}
      </Grid.Container>
    )

    return props.isRfi3
      ? renderRFI3QuestionWindow(
          props.isRfi3Readonly,
          sectionContent,
          beautyPkgCapabilities?.rfi3_answers,
          props.sectionTemplate,
          props.rfiMasterData,
          (sectionRfi3Answers) =>
            setBeautyPkgCapabilities({
              ...beautyPkgCapabilities,
              rfi3_answers: sectionRfi3Answers,
            }),
          props.supplierId,
          props.locationId,
          props.manufacturingTypeId,
          props.sectionData?.rfi3_files,
          (sectionRfiFiles) => {
            setIsTriggerSave(true)
            setBeautyPkgCapabilities({
              ...beautyPkgCapabilities,
              rfi3_files: sectionRfiFiles,
            })
          },
          props.errorCache,
        )
      : sectionContent
  }

  return (
    <SectionWrapper
      sectionTemplate={props.sectionTemplate}
      sectionBody={renderBeautyPackagingBody()}
      setIsTriggerSave={
        !props.isReadonly || props.isRfi3 ? setIsTriggerSave : () => {}
      }
      isRfi3={props.isRfi3}
    />
  )
}

export default BeautyPackagingCapabilities
